import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  LocationOn,
  Visibility,
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface ShuttleBus {
  id: string;
  name: string;
  capacity: number;
}

interface ShuttleSchedule {
  id: string;
  shuttle_bus_id: string;
  departure_date: string;
  departure_time: string;
  pickup_location: string;
  destination: string;
  available_seats: number;
  price: number | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  booked_seats: number;
}

interface ShuttleSchedulerProps {
  shuttle: ShuttleBus;
  onScheduleCreated?: () => void;
  onScheduleUpdated?: () => void;
  onViewBookings?: (scheduleId: string) => void;
}

const ShuttleScheduler: React.FC<ShuttleSchedulerProps> = ({
  shuttle,
  onScheduleCreated,
  onScheduleUpdated,
  onViewBookings,
}) => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState<ShuttleSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ShuttleSchedule | null>(null);
  
  const [scheduleForm, setScheduleForm] = useState({
    departure_date: null as Date | null,
    departure_time: null as Date | null,
    pickup_location: '',
    destination: '',
    available_seats: shuttle.capacity,
    price: null as number | null,
    notes: '',
    status: 'scheduled',
  });

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shuttles/${shuttle.id}/schedules`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
      setError(t('shuttle.admin.errors.fetchSchedules'));
    } finally {
      setLoading(false);
    }
  }, [shuttle.id, t]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);


  const handleOpenDialog = (schedule?: ShuttleSchedule) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setScheduleForm({
        departure_date: new Date(schedule.departure_date),
        departure_time: new Date(`2000-01-01T${schedule.departure_time}`),
        pickup_location: schedule.pickup_location,
        destination: schedule.destination,
        available_seats: schedule.available_seats,
        price: schedule.price,
        notes: schedule.notes || '',
        status: schedule.status,
      });
    } else {
      setEditingSchedule(null);
      setScheduleForm({
        departure_date: null,
        departure_time: null,
        pickup_location: '',
        destination: '',
        available_seats: shuttle.capacity,
        price: null,
        notes: '',
        status: 'scheduled',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSchedule(null);
  };

  const handleSaveSchedule = async () => {
    try {
      if (!scheduleForm.departure_date || !scheduleForm.departure_time) {
        setError(t('shuttle.admin.errors.missingDateTime'));
        return;
      }

      const data = {
        departure_date: format(scheduleForm.departure_date, 'yyyy-MM-dd'),
        departure_time: format(scheduleForm.departure_time, 'HH:mm:ss'),
        pickup_location: scheduleForm.pickup_location,
        destination: scheduleForm.destination,
        available_seats: scheduleForm.available_seats,
        price: scheduleForm.price,
        notes: scheduleForm.notes || null,
        status: scheduleForm.status,
      };

      if (editingSchedule) {
        await api.put(`/schedules/${editingSchedule.id}`, data);
        onScheduleUpdated?.();
      } else {
        await api.post(`/shuttles/${shuttle.id}/schedules`, data);
        onScheduleCreated?.();
      }

      handleCloseDialog();
      fetchSchedules();
    } catch (error: any) {
      console.error('Failed to save schedule:', error);
      setError(error.response?.data?.detail || t('shuttle.admin.errors.saveSchedule'));
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!window.confirm(t('shuttle.admin.confirmDeleteSchedule'))) {
      return;
    }

    try {
      await api.delete(`/schedules/${scheduleId}`);
      fetchSchedules();
    } catch (error: any) {
      console.error('Failed to delete schedule:', error);
      setError(error.response?.data?.detail || t('shuttle.admin.errors.deleteSchedule'));
    }
  };

  const handleStatusChange = async (schedule: ShuttleSchedule, newStatus: string) => {
    try {
      await api.put(`/schedules/${schedule.id}`, { status: newStatus });
      fetchSchedules();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">
          {t('shuttle.admin.schedulesFor')} {shuttle.name}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          {t('shuttle.admin.addSchedule')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {schedules.length === 0 ? (
        <Alert severity="info">
          {t('shuttle.admin.noSchedules')}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('shuttle.admin.date')}</TableCell>
                <TableCell>{t('shuttle.admin.time')}</TableCell>
                <TableCell>{t('shuttle.admin.route')}</TableCell>
                <TableCell align="center">{t('shuttle.admin.occupancy')}</TableCell>
                <TableCell>{t('shuttle.admin.price')}</TableCell>
                <TableCell>{t('shuttle.admin.status')}</TableCell>
                <TableCell align="center">{t('shuttle.admin.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((schedule) => (
                <TableRow key={schedule.id}>
                  <TableCell>
                    {format(new Date(schedule.departure_date), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell>{schedule.departure_time.substring(0, 5)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {schedule.pickup_location} → {schedule.destination}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${schedule.booked_seats}/${schedule.available_seats}`}
                      color={schedule.booked_seats >= schedule.available_seats ? 'error' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {schedule.price ? `€${schedule.price}` : '-'}
                  </TableCell>
                  <TableCell>
                    <FormControl size="small">
                      <Select
                        value={schedule.status}
                        onChange={(e) => handleStatusChange(schedule, e.target.value)}
                      >
                        <MenuItem value="scheduled">
                          <Chip label={t('shuttle.admin.scheduled')} color="primary" size="small" />
                        </MenuItem>
                        <MenuItem value="cancelled">
                          <Chip label={t('shuttle.admin.cancelled')} color="error" size="small" />
                        </MenuItem>
                        <MenuItem value="completed">
                          <Chip label={t('shuttle.admin.completed')} color="success" size="small" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    {onViewBookings && (
                      <Tooltip title={t('shuttle.admin.viewBookings')}>
                        <IconButton
                          size="small"
                          onClick={() => onViewBookings(schedule.id)}
                          color="info"
                        >
                          <Badge badgeContent={schedule.booked_seats} color="primary">
                            <Visibility />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(schedule)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteSchedule(schedule.id)}
                      color="error"
                      disabled={schedule.booked_seats > 0}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Schedule Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSchedule ? t('shuttle.admin.editSchedule') : t('shuttle.admin.newSchedule')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <DatePicker
                label={t('shuttle.admin.departureDate')}
                value={scheduleForm.departure_date}
                onChange={(newValue: any) => setScheduleForm({ ...scheduleForm, departure_date: newValue instanceof Date ? newValue : newValue?.toDate ? newValue.toDate() : newValue })}
                minDate={new Date()}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
              <TimePicker
                label={t('shuttle.admin.departureTime')}
                value={scheduleForm.departure_time}
                onChange={(newValue: any) => setScheduleForm({ ...scheduleForm, departure_time: newValue instanceof Date ? newValue : newValue?.toDate ? newValue.toDate() : newValue })}
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </Box>
            
            <TextField
              fullWidth
              label={t('shuttle.admin.pickupLocation')}
              value={scheduleForm.pickup_location}
              onChange={(e) => setScheduleForm({ ...scheduleForm, pickup_location: e.target.value })}
              placeholder={t('shuttle.admin.customPickupLocation')}
              InputProps={{
                startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <TextField
              fullWidth
              label={t('shuttle.admin.destination')}
              value={scheduleForm.destination}
              onChange={(e) => setScheduleForm({ ...scheduleForm, destination: e.target.value })}
              placeholder={t('shuttle.admin.customDestination')}
              InputProps={{
                startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                type="number"
                label={t('shuttle.admin.availableSeats')}
                value={scheduleForm.available_seats}
                onChange={(e) => setScheduleForm({ ...scheduleForm, available_seats: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: shuttle.capacity }}
                helperText={`${t('shuttle.admin.maxCapacity')}: ${shuttle.capacity}`}
                required
              />
              <TextField
                fullWidth
                type="number"
                label={t('shuttle.admin.price')}
                value={scheduleForm.price || ''}
                onChange={(e) => setScheduleForm({ ...scheduleForm, price: e.target.value ? parseFloat(e.target.value) : null })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('shuttle.admin.notes')}
              value={scheduleForm.notes}
              onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSaveSchedule}
            variant="contained"
            disabled={
              !scheduleForm.departure_date ||
              !scheduleForm.departure_time
            }
          >
            {editingSchedule ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShuttleScheduler;